import { observer } from "mobx-react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAdaptivePractice } from "hooks/useAdaptivePractice";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProduct } from "hooks/useProduct";
import { useProductProgress } from "hooks/useProductProgress";
import { useProfile } from "hooks/useProfile";
import { useRedirect } from "hooks/useRedirect";
import { useLanguage } from "hooks/useSelectedLanguage";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import { useWriting } from "hooks/useWriting";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { ProductType } from "constants/hst-constants";
import { PRODUCT_PAGES_ROUTES, TEST_ENVIRONMENT_ROUTES } from "constants/routes";

import { Heading1, Heading3 } from "styles/elements/Headings";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { trackPlatformEvent } from "analytics/MetabaseTracker/metabase-tracker";
import { STUDENT_MODULES } from "analytics/constants/pageTypes";
import { PLATFORM_EVENT_TYPES } from "analytics/constants/platformEventTypes";
import goToTestImg from "assets/images/go-to-test-image-person-red.svg";
import { Notification } from "components/Notification/Notification";
import { ModuleType } from "models/product/ProductDetails";

import {
  HstProductsWithPlaceholderModules,
  getComingSoonModules,
} from "constants/placeholder-constants";

import { AdaptiveModuleCard } from "./components/AdaptiveModuleCard/AdaptiveModuleCard";
import { ComingSoonModuleCard } from "./components/ComingSoonModuleCard/ComingSoonModuleCard";
import { WritingModuleCard } from "./components/WritingModuleCard/WritingModuleCard";

import { StyledMainSection, StyledTestEnvCard } from "./StudentDashboardPage.styled";

export const StudentDashboardPage: React.FC = observer(() => {
  const { t } = useTranslation("homepage");
  const { currentLanguage } = useLanguage();
  const { canSetInitialProgressFromIntakeTestResults, showComingSoonModules } = useFeatureFlags();

  const { userDetails } = useProfile();
  const selectedProductId = useSelectedProductId();
  const { productDetails } = useProduct();
  const { redirectToUrl } = useRedirect();
  const {
    lastPracticedAdaptiveTopicStatuses,
    fetchLastPracticedAdaptiveTopicStatuses,
    modulesWithIntakeTestScores,
    fetchModulesWithIntakeTestScores,
  } = useAdaptivePractice();
  const {
    lastPracticedWritingTopicStatuses,
    fetchLastPracticedWritingTopicStatuses,
    writingProgressPerModuleSection,
    fetchWritingProgressPerModuleSection,
  } = useWriting();
  const {
    fetchProductGoalLevel,
    productGoalLevel,
    fetchCurrentProgressForAllGoalLevels,
    currentProgressInAllGoalLevels,
  } = useProductProgress();

  // We should only show intake test notification if user still hasn't practiced in the adaptive modules,
  // they have intake scores available for their current goal level and they have permissions to use
  // the init progress from intake test functionality
  const shouldDisplayIntakeTestNotification =
    canSetInitialProgressFromIntakeTestResults &&
    userDetails?.canInitProgressFromIntakeTest &&
    modulesWithIntakeTestScores &&
    modulesWithIntakeTestScores.length > 0 &&
    (!lastPracticedAdaptiveTopicStatuses || lastPracticedAdaptiveTopicStatuses.length === 0);

  // Obtain object with current goal level
  const currentGoalLevel = useMemo(() => {
    if (!productGoalLevel?.goalLevel || !productDetails) {
      return null;
    }

    return productDetails.levels.find(({ level }) => level === productGoalLevel.goalLevel);
  }, [productGoalLevel, productDetails]);

  useEffect(() => {
    if (selectedProductId) {
      fetchLastPracticedAdaptiveTopicStatuses(selectedProductId);
      fetchLastPracticedWritingTopicStatuses(selectedProductId);
      fetchProductGoalLevel(selectedProductId);
      fetchCurrentProgressForAllGoalLevels(selectedProductId);
    }
  }, [selectedProductId]);

  // Reload writing section data whenever user switches their language
  // since progression and section names vary based on selection
  useEffect(() => {
    if (selectedProductId) {
      fetchWritingProgressPerModuleSection(selectedProductId);
    }
  }, [selectedProductId, currentLanguage]);

  // Fetch modules with intake test scores only if that feature is available for the user
  useEffect(() => {
    if (
      selectedProductId &&
      canSetInitialProgressFromIntakeTestResults &&
      userDetails?.canInitProgressFromIntakeTest
    ) {
      fetchModulesWithIntakeTestScores(selectedProductId);
    }
  }, [selectedProductId, canSetInitialProgressFromIntakeTestResults, userDetails]);

  const handleClick = () => {
    trackPlatformEvent({
      module: STUDENT_MODULES.DASHBOARD,
      itemId: PLATFORM_EVENT_TYPES.STUDENT_DASHBOARD_EVENT_TYPES.CLICK_GO_TEST_ENVIRONMENT,
      elementId: "test-env-button-dashboard",
    });
    redirectToUrl(TEST_ENVIRONMENT_ROUTES.LANDING_PAGE);
  };

  const goToModuleWithIntakeTestScores = () => {
    const [moduleWithIntakeTestScores] = modulesWithIntakeTestScores || [];
    if (selectedProductId && moduleWithIntakeTestScores) {
      redirectToUrl(
        buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS, {
          productId: selectedProductId,
          moduleId: moduleWithIntakeTestScores.moduleId,
        }),
      );
    }
  };

  return (
    <StyledMainSection flexDirection="column" rowGap={2}>
      <Heading1>
        {t("studentDashboard.welcomeMessage", { userName: userDetails?.firstName })}
      </Heading1>
      {/* -- Module cards section -- */}
      {selectedProductId && productDetails?.id === selectedProductId && (
        <section>
          <Heading3 marginBottom="2.5rem">
            {t("studentDashboard.currentProgressSection.title")}
          </Heading3>
          <CssFlex flexDirection="row" flexWrap="wrap" gap={2}>
            {productDetails?.modules.map((module) =>
              module.type === ModuleType.ADAPTIVE ? (
                <AdaptiveModuleCard
                  key={module.id}
                  productId={selectedProductId}
                  currentGoalLevel={currentGoalLevel}
                  moduleDetails={module}
                  lastPracticedTopicStatus={lastPracticedAdaptiveTopicStatuses?.find(
                    ({ moduleId }) => moduleId === module.id,
                  )}
                  currentProgressForAllGoalLevels={
                    currentProgressInAllGoalLevels?.find(({ moduleId }) => moduleId === module.id)
                      ?.progressPerLevel
                  }
                />
              ) : (
                <WritingModuleCard
                  key={module.id}
                  productId={selectedProductId}
                  moduleDetails={module}
                  lastPracticedTopicStatus={lastPracticedWritingTopicStatuses?.find(
                    ({ moduleId }) => moduleId === module.id,
                  )}
                  writingProgressPerModuleSection={writingProgressPerModuleSection?.filter(
                    ({ moduleId }) => moduleId === module.id,
                  )}
                />
              ),
            )}
            {/* -- Coming soon modules -- */}
            {showComingSoonModules &&
              HstProductsWithPlaceholderModules.includes(productDetails.id) &&
              getComingSoonModules(t).map((module) => (
                <ComingSoonModuleCard
                  key={module.type}
                  comingSoonModule={module}
                  productId={selectedProductId}
                />
              ))}
            {/* -- Shortcut to test environment (for regular products only) -- */}
            {productDetails?.type === ProductType.REGULAR && (
              <StyledTestEnvCard>
                <Heading3 padding="1rem 0" textAlign="center">
                  {t("studentDashboard.goToTest.cardTitle", { name: userDetails?.firstName })}
                </Heading3>
                <img alt={t("studentDashboard.goToTest.imageAlt")} src={goToTestImg} />
                <CssButton
                  isBlock
                  data-cy="dashboard-test-env-button"
                  variant="primary"
                  onClick={handleClick}
                >
                  {t("studentDashboard.goToTest.button")}
                </CssButton>
              </StyledTestEnvCard>
            )}
          </CssFlex>
        </section>
      )}
      {/* -- Show intake test notification if necessary -- */}
      {shouldDisplayIntakeTestNotification && (
        <Notification dataCy="intake-test-score-available-notice" position="bottomRight">
          {t("studentDashboard.intakeTestAvailableNotice.description")}
          <CssButton
            data-cy="go-to-module-with-intake-test-scores-button"
            onClick={goToModuleWithIntakeTestScores}
            variant="primary"
          >
            {t("studentDashboard.intakeTestAvailableNotice.goToModuleButton")}
          </CssButton>
        </Notification>
      )}
    </StyledMainSection>
  );
});
